<template>
  <div class="form-group"
       :class="{
          'input-group': hasIcon,
          'input-group-focus': focused
       }">
    <slot name="label">
      <label v-if="label" class="control-label">
        {{label}}
      </label>
    </slot>
    <slot name="addonLeft">
      <span v-if="addonLeftIcon" class="input-group-prepend">
        <div class="input-group-text">
          <i :class="addonLeftIcon"></i>
        </div>
      </span>
    </slot>
    <slot>
      <b-alert :show="errorMsg !== ''" variant="danger">{{errorMsg}}</b-alert>
      <input
        :type="inputType"
        :value=value
        :inputmode="inputMode"
        v-bind="inputAttrs"
        v-on="listeners"
        class="form-control"
        ref="imp"
        id="tooltip-target-1"
        aria-describedby="addon-right addon-left">
    </slot>
    <span v-if="isPassword" class="input-group-append">
      <button
        class="input-group-text password-toggle"
        type="button"
        :aria-label="passwordVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
        :title="passwordVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
        @click="passwordVisible = !passwordVisible">
        <b-icon :icon="passwordVisible ? 'eye-slash' : 'eye'"></b-icon>
      </button>
    </span>
    <slot name="addonRight">
      <span v-if="addonRightIcon" class="input-group-append">
        <div class="input-group-text">
          <i :class="addonRightIcon"></i>
        </div>
      </span>
    </slot>
    <slot name="helperText"></slot>
  </div>
</template>
<script>
  export default {
    inheritAttrs: false,
    name: "base-input",
    props: {
      type: {
        type: String,
        default: () => {
          return "texte";
        },
        description: "Input label"
      },
      isMnadatory: {
        type: Boolean,
        default: () => {
          return false;
        }},
      error: {
        type: Array,
        default: () => {
          return [];
        },
        description: "Input label"
      },
      range: {
        type: Object,
        default: () => {
          return null;
        },
        description: "Input label"
      },
      label: {
        type: String,
        description: "Input label"
      },
      value: {
        type: [String, Number],
        description: "Input value"
      },
      addonRightIcon: {
        type: String,
        description: "Input icon on the right"
      },
      addonLeftIcon: {
        type: String,
        description: "Input icon on the left"
      },
    },
    model: {
      prop: 'value',
      event: 'input'
    },
    data() {
      return {
        focused: false,
        errorMsg: '',
        passwordVisible: false,
      }
    },
    computed: {
      isPassword() {
        return ["pwd", "password"].includes(this.$props.type);
      },
      inputType() {
        return this.isPassword && !this.passwordVisible ? "password" : "text";
      },
      inputMode() {
        return this.$props.type === "num" || this.$props.type === "price" ? "decimal" : "text";
      },
      computedPlaceholder() {
        if (this.$props.type === "price") {
          return "0.000";
        }
        return this.$attrs.placeholder;
      },
      inputAttrs() {
        return {
          ...this.$attrs,
          placeholder: this.computedPlaceholder
        };
      },
      statusKey() {
        return this.$attrs.id || this.$attrs.name || this.label || this.$attrs.placeholder || `base-input-${this._uid}`;
      },
      hasIcon() {
        const { addonRight, addonLeft } = this.$slots;
        return this.isPassword || addonRight !== undefined || addonLeft !== undefined || this.addonRightIcon !== undefined || this.addonLeftIcon !== undefined;
      },
      listeners() {
        return {
          ...this.$listeners,
          input: this.onInput,
          blur: this.onBlur,
          focus: this.onFocus
        }
      }
    },
    methods: {
      getStatus(id) {
        const itm = this.$props.error.filter(e => e.id === id)
        if(itm.length === 0) {
          this.$props.error.push({id: id, err: '', isValid: true})
          return this.$props.error[this.$props.error.length -1];
        } else {
          return itm[0];
        }
      },

      onShow(evt) {},
      sanitizeNumericValue(rawValue) {
        const value = String(rawValue ?? '');
        const normalizedComma = value.replace(/,/g, '.');
        const noInvalidChars = normalizedComma.replace(/[^0-9.]/g, '');
        const firstDotIndex = noInvalidChars.indexOf('.');

        if (firstDotIndex === -1) {
          return noInvalidChars;
        }

        const intPart = noInvalidChars.slice(0, firstDotIndex + 1);
        const decimalPart = noInvalidChars.slice(firstDotIndex + 1).replace(/\./g, '');
        return intPart + decimalPart;
      },
      onInput(evt) {
        if(this.$props.type === 'num' || this.$props.type === 'price' ) {
          const sanitizedValue = this.sanitizeNumericValue(evt.target.value);
          if (evt.target.value !== sanitizedValue) {
            evt.target.value = sanitizedValue;
          }

          evt.target.className = "form-control";
          const err = this.getStatus(this.statusKey);
          err.isValid = true;
          err.err = this.errorMsg =  '';

          const hasNumericValue = sanitizedValue !== '' && sanitizedValue !== '.';
          const numericValue = hasNumericValue ? Number(sanitizedValue) : null;

          if(hasNumericValue && this.$props.range?.min > numericValue) {
            err.isValid = false;
            err.err = this.errorMsg = 'Valeur min autorisée = '+this.$props.range.min;
            evt.target.className = "form-control error";
          }
          if(hasNumericValue && this.$props.range?.max < numericValue) {
            err.isValid = false;
            err.err = this.errorMsg = 'Valeur max autorisée = '+this.$props.range.max;
            evt.target.className = "form-control error";
          }
          if( this.$props.type === 'price' ) {
            this.$emit('input', sanitizedValue);
            return;
          }
          this.$emit('input', sanitizedValue);
        } 
        else {
          this.$emit('input', evt.target.value)
        }
        
      },
      
      onFocus() {
        this.focused = true;
        this.$refs.imp.className = "form-control";
        const err = this.getStatus(this.statusKey);
        err.isValid = true;
        err.err = this.errorMsg =  '';
      },

      onBlur() {
        this.focused = false;
        if(this.$props.type === 'price' && this.$props.value !== '' && this.$props.value !== null && this.$props.value !== undefined) {
          const sanitizedValue = this.sanitizeNumericValue(this.$props.value);
          if (sanitizedValue !== '' && sanitizedValue !== '.') {
            this.$emit('input', Number(sanitizedValue).toFixed(3));
          }
        }
        if(this.$props.isMnadatory && this.$props.value ==='') {
           this.$refs.imp.className = "form-control error";
           const err = this.getStatus(this.statusKey);
            err.isValid = false;
            err.err = this.errorMsg =  'Champs obligatoire';
        }
      }
    },
    mounted() {
        
      if(this.$props.isMnadatory && this.$props.value ==='') {
           const err = this.getStatus(this.statusKey);
            err.isValid = false;
            err.err = '';
        }
      },
  }
</script>
<style>
.alert-danger {
  color: #ffffff !important;
    background-color: #ff9a02 !important;
    border-color: #ff9a02 !important;
}
.error {
  background-color: #fff !important;
  border-color: #ff9a02 !important;
}
.alert{
  position: relative !important;
  font-size: 11px !important;
  padding: 4px 10px !important;
  top: auto !important;
  z-index: 1;
  margin-top: 4px;
  border-radius: 4px;
  display: inline-block;
}
.rtl .alert {
  right: auto;
  margin-left: 0;
  margin-right: 0;
  margin-top: 4px;
}
</style>
